import React, { useState, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { APP_INIT_RESPONSE_TYPE, BUTTON_TYPE, DOMAIN_NAMES } from '../../../global/constants';
import { SignupInterface } from '../../../interfaces/SignUpInterface';
import { ROUTES } from '../../../routes/urls';
import { signUp, validateIsLoggedIn } from '../../../services/authServices';
import Button from '../../../component/button';
import { trackAnalyticActivity, trackPage } from '../../../services/analyticsService';
import AppLogo from '../../../component/appLogo';
import { getCookie, getCurrentDomain, isEmpty, isReactNative } from '../../../helpers/common';
import { SignUpPayloadInterface } from '../../../interfaces/AuthInterface';
import {
  Content,
  DividerLine,
  DividerRow,
  DividerText,
  Form,
  FormBlock,
  FormContent,
  FormSection,
  Group,
  Icon,
  InputValue,
  TextLine,
  Title,
  TopView,
  Viewer
} from '../styles';
import { VIEW_ANALYTICS } from '../../../global/analyticsConstants';
import LayoutWithoutNavbar from '../../../component/layoutWithoutNavbar';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { useMobileDevice } from '../../../global/useMobile';
import GoogleButton from '../../../component/googleButton';

const Signup: React.FC = () => {
  //State
  const [inputDetails, setInputDetails] = useState<SignupInterface>({
    name: '',
    email: '',
    password: ''
  });
  const [referCodeValue, setReferCodeValue] = useState('');
  const [loading, setLoading] = useState(false);

  //mobx variable
  const { appStore } = rootStore;
  const { authLoading } = appStore;

  //Other variable
  const currentDomain = getCurrentDomain();
  const history = useHistory();
  const isMobile = useMobileDevice();

  // for name change
  const onChangeName = (value: string) => {
    setInputDetails({ ...(inputDetails || {}), name: value });
  };

  // for email change
  const onChangeEmail = (value: string) => {
    setInputDetails({ ...(inputDetails || {}), email: value });
  };

  // for password change
  const onChangePassword = (value: string) => {
    setInputDetails({ ...(inputDetails || {}), password: value });
  };

  useEffect(() => {
    trackPage({ visit_url: 'signup' });
    trackAnalyticActivity(VIEW_ANALYTICS.SIGN_UP_PAGE);
    if (validateIsLoggedIn()) {
      history.push(ROUTES.INBOX);
    }
    const cookieValue = getCookie('cross_domain_referral');
    if (cookieValue) setReferCodeValue(cookieValue);
  }, [history]);

  // for signup form submit
  const onClickSignUp = useCallback(
    async (e: React.SyntheticEvent) => {
      e.preventDefault();
      const payload: SignUpPayloadInterface = {
        ...(inputDetails || {}),
        confirmPassword: inputDetails.password,
        appName: 'WebAppProject'
      };
      if (!isEmpty(referCodeValue)) payload.referCode = referCodeValue;
      const result = await signUp(payload);
      if (result && result.type === APP_INIT_RESPONSE_TYPE.REDIRECT && result.path) {
        history.push(result.path);
      } else if (result && result.type === APP_INIT_RESPONSE_TYPE.SUCCESS) {
        history.push(ROUTES.INBOX);
      }
    },
    [inputDetails, referCodeValue, history]
  );

  return (
    <>
      <LayoutWithoutNavbar>
        <Group isSignupPage={true} className='windows-onboarding-flow'>
          <Content isSignupPage={true}>
            <TopView>
              <Icon>
                <AppLogo height={60} />
              </Icon>
              <Title>Create New Account</Title>
              <FormContent>
                <Form onSubmit={onClickSignUp} isSignupPage={true}>
                  {!isReactNative() && DOMAIN_NAMES.includes(currentDomain) && (
                    <>
                      {/* google Button */}
                      <GoogleButton loading={loading} setLoading={setLoading} />
                      {/* OR Divider */}
                      <DividerRow>
                        <DividerLine />
                        <DividerText>or</DividerText>
                        <DividerLine />
                      </DividerRow>
                    </>
                  )}
                  {/* form section */}
                  <FormSection isSignupPage={true}>
                    <FormBlock>
                      <InputValue
                        type='text'
                        placeholder='Name'
                        onChange={(e) => onChangeName(e.target.value)}
                        autoFocus={true}
                        isSignupPage={true}
                      />
                      <InputValue
                        type='email'
                        placeholder='Email Address'
                        onChange={(e) => onChangeEmail(e.target.value)}
                        className='password'
                        isSignupPage={true}
                      />
                      <InputValue
                        type='password'
                        placeholder='Password'
                        onChange={(e) => onChangePassword(e.target.value)}
                        className='password-input'
                        isSignupPage={true}
                      />
                    </FormBlock>
                    <Button
                      variant={BUTTON_TYPE.PRIMARY}
                      buttonType='submit'
                      title='Sign Up'
                      size={isMobile ? 'large' : 'medium'}
                      isLoading={authLoading || loading}
                      width='100%'
                    />
                  </FormSection>
                </Form>
                <Button
                  buttonType='button'
                  variant={BUTTON_TYPE.TERTIARY}
                  title=' Go back to login'
                  size={isMobile ? 'large' : 'medium'}
                  onClick={() => history.push(ROUTES.LOGIN)}
                  disabled={authLoading || loading}
                  width='100%'
                  className='button-hover'
                />
              </FormContent>
            </TopView>
            <TextLine>
              By signing up, you confirm that you have read and agree to{' '}
              <Viewer isSignupPage={true} href='https://www.teamcamp.app/terms-of-use'>
                Terms of Use
              </Viewer>{' '}
              and{' '}
              <Viewer isSignupPage={true} href='https://www.teamcamp.app/privacy-policy'>
                Privacy Policy
              </Viewer>
            </TextLine>
          </Content>
        </Group>
      </LayoutWithoutNavbar>
    </>
  );
};

export default observer(Signup);
